import { ReactNode } from "react";
import { NavLink } from "react-router-dom";
import { ChevronLeft, LayoutDashboard, Sparkles, Users, Lightbulb, Headphones } from "lucide-react";
 
type SidebarLink = {
  title: string;
  to: string;
  icon: ReactNode;
  end?: boolean;
};
 
interface SidebarProps {
  menuItems?: SidebarLink[];
  collapsed: boolean;
  onToggle: () => void;
  isMobile?: boolean;
}
 
const defaultMenuItems: SidebarLink[] = [
  {
    title: "Dashboard",
    to: "/user-dashboard",
    icon: <LayoutDashboard className="w-4 h-4" />,
    end: true,
  },
  {
    title: "Innovation Programs",
    to: "/user-dashboard/hackathons",
    icon: <Sparkles className="w-4 h-4" />,
  },
  {
    title: "Project Team",
    to: "/user-dashboard/my-teams",
    icon: <Users className="w-4 h-4" />,
  },
  {
    title: "Innovation Hub",
    to: "/user-dashboard/idea-list",
    icon: <Lightbulb className="w-4 h-4" />,
  },
  {
    title: "Expert Connect",
    to: "/user-dashboard/chat",
    icon: <Headphones className="w-4 h-4" />,
  },
];
 
export default function Sidebar({ menuItems, collapsed, onToggle, isMobile }: SidebarProps) {
  const items = menuItems ?? defaultMenuItems;
 
  return (
    <aside
      className={`h-screen border-r border-[#E0E4F0] bg-[#EBF0FF] overflow-y-auto transition-[width] duration-300 ease-in-out ${
        collapsed ? "w-[80px]" : "w-[230px]"
      }`}
    >
      {/* COLLAPSE BUTTON - Hidden on mobile */}
      <div className="hidden md:flex items-center justify-between px-4 h-[60px] cursor-pointer hover:text-blue-600 mx-auto">
        <span
          className={`text-sm font-medium text-gray-700 whitespace-nowrap overflow-hidden transition-[max-width,opacity] duration-300 ease-in-out ${
            collapsed ? "max-w-0 opacity-0" : "max-w-[120px] opacity-100"
          }`}
        >
          Collapse
        </span>
        <button
          onClick={onToggle}
          className="p-2 rounded"
        >
          <ChevronLeft
            className={`w-5 h-5 text-gray-700 transition-transform duration-300 ease-in-out ${
              collapsed ? "rotate-180" : ""
            }`}
          />
        </button>
      </div>
 
      {/* MENU */}
      <nav className="mt-2 relative">
        {items.map((item, index) => (
          <SidebarItem
            key={`${item.title}-${index}`}
            title={item.title}
            icon={item.icon}
            collapsed={collapsed}
            to={item.to}
            end={item.end}
            isMobile={isMobile}
            onNavigate={isMobile ? onToggle : undefined}
          />
        ))}
      </nav>
    </aside>
  );
}
 
function SidebarItem({
  title,
  icon,
  collapsed,
  to,
  end,
  isMobile,
  onNavigate,
}: {
  title: string;
  icon: ReactNode;
  collapsed: boolean;
  to: string;
  end?: boolean;
  isMobile?: boolean;
  onNavigate?: () => void;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onNavigate}
      className={({ isActive }) =>
        `flex items-center h-[36px] gap-2 sm:gap-3 px-2 sm:px-4 py-6 cursor-pointer rounded-lg mx-1 sm:mx-3 ${
          isActive
            ? "bg-[linear-gradient(90deg,rgba(0,88,171,0.07)_0%,rgba(29,184,242,0.03)_100%)] border-l-2 border-l-[#0058AB] text-blue-600"
            : "text-gray-700"
        }`
      }
    >
      <span className="flex-shrink-0 w-4 h-4">{icon}</span>
      <span
        className={`text-xs sm:text-sm font-medium whitespace-nowrap overflow-hidden transition-[max-width,opacity] duration-300 ease-in-out ${
          collapsed && !isMobile ? "max-w-0 opacity-0" : "max-w-[160px] opacity-100"
        }`}
      >
        {title}
      </span>
    </NavLink>
  );
}
